import React, { useContext, useMemo, useState, useEffect } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";
import SocketContext from '../../context/SocketContext';

function buildDefaultImages() {
  // Numbered mode and static image option
  return [
    {
      id: 'numbered',
      label: 'Numbered Tiles Only',
      src: null,
    },
    {
      id: 'image1',
      label: 'Sample Image',
      src: '/images/slidingtiles/image1.png',
    },
  ];
}

function getTileBackgroundPosition(tileValue, gridSize) {
  const solvedIndex = tileValue - 1;
  const col = solvedIndex % gridSize;
  const row = Math.floor(solvedIndex / gridSize);
  const max = Math.max(gridSize - 1, 1);
  return `${(col / max) * 100}% ${(row / max) * 100}%`;
}


function SlidingTilesGame({ roomId, gameState, gamePlayers = [], user = {}, onGameAction, socket: socketProp, isSpectator = false }) {
  const socketFromContext = useContext(SocketContext);
  const socket = socketProp || socketFromContext;

  // --- Spectator Perspective logic (modular, opt-in, matches Canasta) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const defaultImages = useMemo(() => buildDefaultImages(), []);
  const [selectedImageId, setSelectedImageId] = useState(defaultImages[0]?.id || 'numbered');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadError, setUploadError] = useState('');

  // For all per-player state, use the selected perspective
  const myId = useMemo(() => {
    for (const id of perspectiveIds) {
      if (gameState?.boards && gameState.boards[id]) return id;
    }
    return socket?.id || user?.socketId || null;
  }, [perspectiveIds, gameState?.boards, socket?.id, user?.socketId]);

  const boards = gameState?.boards || {};
  const myBoard = myId ? boards[myId] || null : null;
  const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;
  const gridSize = Number(gameState?.gridSize || gameState?.config?.gridSize || 4);
  const activeImage = uploadedImage || (selectedImageId !== 'numbered' ? defaultImages.find((image) => image.id === selectedImageId)?.src : null);
  const finished = !!gameState?.finished;
  const winners = Array.isArray(gameState?.winners) ? gameState.winners : [];
  // const winnerNames = winners.map((winnerId) => {
  //   const winner = players.find((player) => player.socketId === winnerId || player.userId === winnerId || player.id === winnerId);
  //   return winner?.username || winnerId;
  // });

  const handleMove = async (tileIndex) => {
    if (!myBoard || finished) return;
    await submitAction({ type: 'move', tileIndex });
  };

  const handleReset = async () => {
    if (!myBoard || finished) return;
    await submitAction({ type: 'reset-board' });
  };

  const handleUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(typeof reader.result === 'string' ? reader.result : null);
      setUploadError('');
    };
    reader.onerror = () => {
      setUploadError('That image could not be loaded.');
    };
    reader.readAsDataURL(file);
  };

  // Progress rows: always show all players, but highlight the perspective
  const progressRows = players.map((player) => {
    const playerId = player.socketId || player.userId || player.id;
    const board = boards[playerId];
    const correctTiles = board?.correctTiles || 0;
    const totalTiles = board?.totalTiles || gridSize * gridSize;
    const progress = totalTiles > 0 ? Math.round((correctTiles / totalTiles) * 100) : 0;
    // Highlight the perspective (spectator or local)
    const isMe = perspectiveIds.includes(String(playerId));
    return {
      id: playerId,
      username: player.username || 'Player',
      moveCount: board?.moveCount || 0,
      progress,
      solved: !!board?.solved,
      isWinner: winners.includes(playerId),
      isMe,
    };
  });


  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  if (!gameState) {
    return (
      <div className="rounded-3xl bg-white p-6 text-gray-900 shadow-xl dark:bg-slate-900 dark:text-gray-100">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        )}
        Loading puzzle...
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-3 text-gray-900 dark:text-gray-100 sm:p-4">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      <div className="overflow-hidden rounded-[28px] border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80">
        <div className="flex flex-col gap-0">
          <div className="p-4 sm:p-6 col-span-full">

            <div className="mb-4 flex flex-wrap gap-2">
              {/* Numbers only option */}
              <button
                key="numbered"
                type="button"
                onClick={() => {
                  setSelectedImageId('numbered');
                  setUploadedImage(null);
                }}
                className={`overflow-hidden rounded-2xl border transition ${!uploadedImage && selectedImageId === 'numbered' ? 'border-cyan-500 ring-2 ring-cyan-300 dark:border-cyan-400 dark:ring-cyan-900' : 'border-slate-200 hover:border-cyan-300 dark:border-slate-700 dark:hover:border-cyan-700'}`}
                title="Numbered Tiles Only"
              >
                <div className="h-14 w-14 flex items-center justify-center bg-cyan-300 text-cyan-900 font-bold text-lg sm:h-16 sm:w-16 rounded-xl border border-cyan-400">123</div>
              </button>
              {/* Sample image option */}
              <button
                key="image1"
                type="button"
                onClick={() => {
                  setSelectedImageId('image1');
                  setUploadedImage(null);
                }}
                className={`overflow-hidden rounded-2xl border transition ${!uploadedImage && selectedImageId === 'image1' ? 'border-cyan-500 ring-2 ring-cyan-300 dark:border-cyan-400 dark:ring-cyan-900' : 'border-slate-200 hover:border-cyan-300 dark:border-slate-700 dark:hover:border-cyan-700'}`}
                title="Sample Image"
              >
                <div className="h-14 w-14 bg-cover bg-center sm:h-16 sm:w-16 rounded-xl border border-cyan-400" style={{ backgroundImage: `url('/images/slidingtiles/image1.png')` }} />
              </button>
              {/* Upload image option */}
              <label className="flex cursor-pointer items-center justify-center rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-cyan-400 hover:text-cyan-700 dark:border-slate-700 dark:text-slate-200 dark:hover:border-cyan-600 dark:hover:text-cyan-300">
                Use Local Image
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>
              {uploadedImage && (
                <button
                  type="button"
                  onClick={() => setUploadedImage(null)}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-slate-400 dark:border-slate-700 dark:text-slate-200 dark:hover:border-slate-500"
                >
                  Use numbered tiles
                </button>
              )}
            </div>

            {uploadError && (
              <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/60 dark:text-rose-200">
                {uploadError}
              </div>
            )}

            <div className="flex flex-col gap-4">
              <div>
                <div
                  className="mx-auto grid aspect-square w-full max-w-[min(82vw,620px)] gap-[2px] bg-black/20"
                  style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
                >
                  {(myBoard?.tiles || []).map((tileValue, index) => {
                    if (tileValue === 0) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className="border border-dashed border-white/20 bg-black/15"
                        />
                      );
                    }

                    // Numbered mode: show aqua tile with number only
                    if (!activeImage) {
                      return (
                        <button
                          key={`tile-${tileValue}`}
                          type="button"
                          onClick={() => handleMove(index)}
                          disabled={finished}
                          className="group relative overflow-hidden border border-cyan-400 bg-cyan-300 text-cyan-900 font-bold text-xl transition duration-150 hover:scale-[1.01] hover:border-cyan-500 disabled:cursor-default disabled:hover:scale-100"
                          title={`Tile ${tileValue}`}
                        >
                          {tileValue}
                        </button>
                      );
                    }

                    // Image mode
                    const backgroundSize = `${gridSize * 100}% ${gridSize * 100}%`;
                    return (
                      <button
                        key={`tile-${tileValue}`}
                        type="button"
                        onClick={() => handleMove(index)}
                        disabled={finished}
                        className="group relative overflow-hidden border border-white/35 shadow-[0_10px_24px_rgba(15,23,42,0.25)] transition duration-150 hover:scale-[1.01] hover:border-white/60 disabled:cursor-default disabled:hover:scale-100"
                        style={{
                          backgroundImage: `url(${activeImage})`,
                          backgroundSize,
                          backgroundPosition: getTileBackgroundPosition(tileValue, gridSize),
                        }}
                        title={`Tile ${tileValue}`}
                      >
                        <div className="absolute left-2 top-2 bg-black/55 px-2 py-0.5 text-xs font-bold text-white backdrop-blur">
                          {tileValue}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!myBoard || finished}
                  className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                >
                  Reset to starting shuffle
                </button>
                <div className="rounded-3xl border border-slate-200 bg-white/85 p-4 text-sm shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
                  <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Target</div>
                  <div className="mt-3 aspect-square overflow-hidden  border border-slate-200 bg-white flex items-center justify-center dark:border-slate-700">
                    <div
                      className="grid w-full h-full"
                      style={{
                        gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${gridSize}, minmax(0, 1fr))`,
                        gap: '2px',
                      }}
                    >
                      {Array.from({ length: gridSize * gridSize }, (_, i) => {
                        // Spare square (bottom-right)
                        if (i === gridSize * gridSize - 1) {
                          return (
                            <div
                              key="target-spare"
                              className="border border-dashed border-white/20 bg-black/15 dark:bg-slate-900 dark:border-slate-800"
                              style={{ aspectRatio: '1 / 1' }}
                            />
                          );
                        }
                        if (!activeImage) {
                          // Numbered mode
                          return (
                            <div
                              key={`target-tile-${i + 1}`}
                              className="flex items-center justify-center border border-cyan-400 bg-cyan-300 text-cyan-900 font-bold text-xl"
                              style={{ aspectRatio: '1 / 1' }}
                            >
                              {i + 1}
                            </div>
                          );
                        }
                        // Image mode
                        const backgroundSize = `${gridSize * 100}% ${gridSize * 100}%`;
                        const col = i % gridSize;
                        const row = Math.floor(i / gridSize);
                        const backgroundPosition = `${(col / (gridSize - 1)) * 100}% ${(row / (gridSize - 1)) * 100}%`;
                        return (
                          <div
                            key={`target-image-tile-${i + 1}`}
                            className="relative w-full h-full bg-cover bg-center border border-white/35"
                            style={{
                              backgroundImage: `url(${activeImage})`,
                              backgroundSize,
                              backgroundPosition,
                              aspectRatio: '1 / 1',
                            }}
                          >
                            <div className="absolute left-2 top-2 bg-black/55 px-2 py-0.5 text-xs font-bold text-white backdrop-blur">
                              {i + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Race Board moved below the board */}
            <div className="mt-8">
              <div className="rounded-3xl bg-white p-4 shadow-sm dark:bg-slate-900">
                <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Race Board</div>
                <div className="mt-4 space-y-3">
                  {progressRows.map((row) => (
                    <div
                      key={row.id}
                      className={`rounded-2xl border px-3 py-3 ${row.isWinner ? 'border-emerald-400 bg-emerald-100/80 dark:border-emerald-700 dark:bg-emerald-950/50' : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">
                          {row.username}{row.isMe ? ' (You)' : ''}
                        </div>
                        <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                          {row.solved ? 'Solved' : `${row.progress}%`}
                        </div>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                        <div className={`h-full rounded-full ${row.solved ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${row.progress}%` }} />
                      </div>
                      <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">Moves {row.moveCount}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>
          </div>
          {/* ...existing code... */}
        </div>
      </div>
  
  );
}

SlidingTilesGame.Guide = function SlidingTilesGuide() {
  return (
    <div className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
      <div className="font-bold">How to Play Sliding Tiles</div>
      <div>Tap or click a tile next to the empty space to slide it.</div>
      <div>Rebuild the full image in the correct order before the other players do.</div>
      <div>The reset button restores your board to the round's original shuffled layout.</div>
      <div>You can swap between using just numbers only or using a local image (only you see the image and it is not uploaded anywhere).</div>
      <div className="font-bold">Tips for Solving</div>
      <div><b>Solve Row by Row (Top-Down):</b> Start by placing tile #1 in the top-left corner, followed by #2, then #3, etc., until the top row is finished.</div>
      <div><b>Handle the Last Two Tiles:</b> For a row, place the last two tiles (e.g., 3 and 4) by putting them in a vertical column to the right and then sliding them into their spots.</div>
      <div><b>Keep Solved Tiles Locked:</b> Once a row or column is correctly placed, avoid moving those tiles.</div>
      <div><b>The Final Pieces:</b> For the last 3-4 tiles, rotate them around the remaining empty spaces until they fall into the correct sequence.</div>
      <div><a href="https://www.wikihow.com/Solve-Slide-Puzzles" className='underline' target="_blank" rel="noopener noreferrer">WikiHow tips on solving Slide Puzzles</a></div>
    </div>
  );
};

export default SlidingTilesGame;